import { useEffect, useRef, useState } from 'react'
import { Github, CheckCircle2, AlertCircle, ExternalLink, Unplug, SlidersHorizontal, X } from 'lucide-react'
import Spinner from '../Spinner'
import type { Project } from '../../data/projectTypes'
import GithubTestingQuestions from './GithubTestingQuestions'
import { buildGithubSettings, applyGithubTestingAnswers, normalizeGithubRepositoryUrl, summarizeGithubTesting, type GithubTestingAnswers } from '../../lib/githubTesting'
import type { GithubPanelFocus } from '../../../netlify/functions/lib/project-chat-types'
import { useGetGithubAppStatusQuery, useUpdateProjectMutation } from '../../services/projectDataApi'

// Map the agent-facing panel focus ('pull-requests') onto GithubTestingQuestions' internal step name
// ('prs'). 'all' means walk the whole flow.
type QuestionFocus = 'all' | 'main-branch' | 'prs'
function toQuestionFocus(focus: GithubPanelFocus): QuestionFocus {
  return focus === 'pull-requests' ? 'prs' : focus === 'main-branch' ? 'main-branch' : 'all'
}

// The GitHub connect/disconnect panel rendered INLINE in the running-project chat, surfaced when the
// QA agent calls the manage_github tool. It lets the owner link this project to a repository (install
// the Replay QA GitHub App if needed, then link with sensible testing defaults) or disconnect the
// repo already connected — the two things the chat previously had to defer to the settings page.
// Compact styling reuses chat.css .rp-card*, matching GithubConnectChatCard (the onboarding variant).

const POLL_MS = 5000

function normalizeUrl(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

// Parse owner/repo out of a GitHub repository URL, returning null when it isn't one.
function parseGithubRepo(raw: string): string | null {
  const normalized = normalizeGithubRepositoryUrl(raw)
  return normalized ? new URL(normalized).pathname.slice(1) : null
}

export default function ProjectGithubChatCard({
  project,
  focus = 'all',
  initialRepoUrl,
  onClose,
  onResult,
  onConnected,
}: {
  project: Project
  // Which part of the flow the agent asked to focus on. Only meaningful for an already-connected repo
  // (it opens straight to that testing question); linking a new repo always asks both questions.
  focus?: GithubPanelFocus
  // When the user named a repository in chat, the agent passes it through so the connect form does
  // not make them repeat themselves. Installation validation and linking still happen normally.
  initialRepoUrl?: string
  onClose: () => void
  // Append a short confirmation line to the transcript after a successful connect/disconnect.
  onResult?: (text: string) => void
  // Continue work that had to pause until a repository was connected.
  onConnected?: () => void
}) {
  const connected = !!project.github?.repoUrl && !!project.github?.mainBranch
  return connected
    ? <ConnectedPanel project={project} focus={focus} onClose={onClose} onResult={onResult} />
    : <ConnectPanel project={project} initialRepoUrl={initialRepoUrl} onClose={onClose} onResult={onResult} onConnected={onConnected} />
}

// Already linked → show the repo + its current testing settings, let the user change them (the shared
// testing questions), and offer a two-step Disconnect (keeps the project, drops the GitHub link).
function ConnectedPanel({ project, focus, onClose, onResult }: { project: Project; focus: GithubPanelFocus; onClose: () => void; onResult?: (text: string) => void }) {
  const [updateProject] = useUpdateProjectMutation()
  const questionFocus = toQuestionFocus(focus)
  const [confirming, setConfirming] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  // When the agent targeted a single question ('main-branch' / 'pull-requests'), skip the summary and
  // open straight into that one question — that's the whole point of focusing the panel.
  const [editing, setEditing] = useState(questionFocus !== 'all')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const github = project.github
  const repoUrl = github?.repoUrl ?? ''
  const repoName = parseGithubRepo(repoUrl) ?? repoUrl
  const current: GithubTestingAnswers = {
    mainBranchTesting: github?.mainBranchTesting ?? 'none',
    mainBranchDeploymentUrl: github?.mainBranchDeploymentUrl ?? '',
    prTesting: github?.prTesting ?? 'none',
  }

  async function disconnect() {
    setDisconnecting(true)
    setError(null)
    try {
      await updateProject({ id: project.id, data: { github: null } }).unwrap()
      onResult?.(`✓ Disconnected the project from ${repoName}. QA keeps running on the app URL; you can reconnect a repository anytime.`)
      onClose()
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to disconnect from GitHub')
      setDisconnecting(false)
    }
  }

  // Persist changed main-branch / PR testing onto the existing connection (repo, token, and the
  // file-issues / require-approval flags are preserved — see applyGithubTestingAnswers).
  async function saveTesting(answers: GithubTestingAnswers) {
    if (!github) return
    setSaving(true)
    setError(null)
    try {
      await updateProject({ id: project.id, data: { github: applyGithubTestingAnswers(github, answers) } }).unwrap()
      onResult?.(`✓ Updated GitHub testing for ${repoName}. ${summarizeGithubTesting(answers)}.`)
      onClose()
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to update GitHub testing settings')
      setSaving(false)
    }
  }

  return (
    <div className="rp-card" data-testid="project-github-card" data-status="connected">
      <div className="rp-card__head">
        <Github size={15} className="rp-card__icon" aria-hidden />
        <span className="rp-card__title">Connected to <code>{repoName}</code></span>
        <button type="button" className="rp-card__dismiss" onClick={onClose} aria-label="Close GitHub panel" data-testid="project-github-close">
          <X size={14} aria-hidden />
        </button>
      </div>

      {editing ? (
        saving ? (
          <p className="rp-card__waiting" data-testid="project-github-saving">
            <Spinner size={13} className="rp-card__icon" /> Saving…
          </p>
        ) : (
          <GithubTestingQuestions
            repoLabel={repoName}
            focus={questionFocus}
            current={current}
            defaultDeploymentUrl={github?.mainBranchDeploymentUrl || project.target_url || ''}
            onComplete={saveTesting}
          />
        )
      ) : (
        <>
          <p className="rp-card__text">
            Linked to <a href={repoUrl} target="_blank" rel="noopener noreferrer">{repoName}</a> — {summarizeGithubTesting(current)}.
          </p>
          <div className="rp-card__actions">
            <button type="button" className="rp-card__copy" onClick={() => setEditing(true)} data-testid="project-github-edit-testing" data-analytics-event="chat-project-github-edit-testing">
              <SlidersHorizontal size={13} aria-hidden /> Change testing settings
            </button>
            {confirming ? (
              <>
                <button type="button" className="rp-card__copy" onClick={disconnect} disabled={disconnecting} data-testid="project-github-disconnect-confirm" data-analytics-event="chat-project-github-disconnect-confirm">
                  <Unplug size={13} aria-hidden /> {disconnecting ? 'Disconnecting…' : 'Confirm disconnect'}
                </button>
                <button type="button" className="rp-card__copy" onClick={() => setConfirming(false)} disabled={disconnecting}>
                  Cancel
                </button>
              </>
            ) : (
              <button type="button" className="rp-card__copy" onClick={() => setConfirming(true)} data-testid="project-github-disconnect" data-analytics-event="chat-project-github-disconnect">
                <Unplug size={13} aria-hidden /> Disconnect
              </button>
            )}
          </div>
        </>
      )}
      {error && <p className="rp-card__text">{error}</p>}
    </div>
  )
}

// Not linked → enter a repo URL, check the App install status, then link (or install the App first).
function ConnectPanel({ project, initialRepoUrl, onClose, onResult, onConnected }: { project: Project; initialRepoUrl?: string; onClose: () => void; onResult?: (text: string) => void; onConnected?: () => void }) {
  const [updateProject] = useUpdateProjectMutation()
  const [repoUrl, setRepoUrl] = useState(() => initialRepoUrl ? normalizeGithubRepositoryUrl(initialRepoUrl) ?? '' : '')
  const [error, setError] = useState<string | null>(null)

  const repoFullName = parseGithubRepo(repoUrl)
  const statusQuery = useGetGithubAppStatusQuery({ repo: repoFullName ?? '' }, {
    skip: !repoFullName,
    pollingInterval: POLL_MS,
    skipPollingIfUnfocused: true,
  })
  const status = statusQuery.data
  const checking = statusQuery.isLoading || (statusQuery.isFetching && !status)
  const displayError = error ?? (statusQuery.error ? 'Failed to check the GitHub App' : null)
  const installed = !!(status && status.installed && status.installationId)
  // Watch the project's own app URL for new main-branch deployments — the app QA already tests.
  const deploymentUrl = (project.target_url ?? '').trim()
  // Link exactly once when the App turns out installed (the effect below fires on each poll).
  const linked = useRef(false)

  // Once the App is installed, link immediately with the same sensible defaults onboarding uses —
  // detect new main-branch deployments to the project's own app URL, PR testing off — instead of
  // asking the testing questions here (they overflowed the chat). Fine-tuning stays available via the
  // Connected panel's "Change testing settings" and the GitHub settings tab.
  async function link() {
    if (!repoFullName || !installed) return
    const answers: GithubTestingAnswers = { mainBranchTesting: 'detect', mainBranchDeploymentUrl: deploymentUrl, prTesting: 'none' }
    const github = buildGithubSettings(normalizeUrl(repoUrl), status?.defaultBranch || 'main', answers)
    setError(null)
    try {
      await updateProject({ id: project.id, data: { github } }).unwrap()
      onResult?.(`✓ Connected this project to ${status?.repoFullName ?? repoFullName}. ${summarizeGithubTesting(answers)}. Ask here anytime to fine-tune deployment or pull-request testing, or to disconnect the repository.`)
      onClose()
      onConnected?.()
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to link the project to GitHub')
      linked.current = false
    }
  }

  // Auto-link as soon as the App is confirmed installed (mirrors onboarding's onGithubReady).
  useEffect(() => {
    if (installed && !linked.current) { linked.current = true; void link() }
  }, [installed])

  const slug = status?.slug || (import.meta.env.VITE_GITHUB_APP_SLUG as string | undefined) || 'replay-qa'
  const installUrl = `https://github.com/apps/${slug}/installations/new`

  return (
    <div className="rp-card" data-testid="project-github-card" data-status="disconnected">
      <div className="rp-card__head">
        <Github size={15} className="rp-card__icon" aria-hidden />
        <span className="rp-card__title">Connect a GitHub repository</span>
        <button type="button" className="rp-card__dismiss" onClick={onClose} aria-label="Close GitHub panel" data-testid="project-github-close">
          <X size={14} aria-hidden />
        </button>
      </div>
      <p className="rp-card__text">
        Link this project to a repository to run QA on its pull requests and main-branch deployments.
      </p>
      <div className="rp-card__field">
        <input
          type="url"
          className="rp-card__input"
          value={repoUrl}
          onChange={(e) => { setRepoUrl(e.target.value); setError(null) }}
          placeholder="https://github.com/owner/repo"
          autoComplete="off"
          aria-label="GitHub repository URL"
          data-testid="project-github-url-input"
        />
      </div>

      {repoFullName && (
        checking && !status ? (
          <p className="rp-card__waiting" data-testid="project-github-checking">
            <Spinner size={13} className="rp-card__icon" /> Checking the GitHub App…
          </p>
        ) : installed ? (
          // Installed → auto-linking (see the effect). Show a brief confirmation; the transcript gets
          // the success line and the panel closes.
          <p className="rp-card__waiting" data-testid="project-github-linking">
            <CheckCircle2 size={13} className="rp-card__icon rp-card__icon--ok" aria-hidden /> Connecting <code>{status!.repoFullName}</code>…
          </p>
        ) : (
          <>
            <p className="rp-card__text" data-testid="project-github-not-installed">
              <AlertCircle size={13} className="rp-card__icon" aria-hidden /> The Replay QA GitHub App isn&apos;t installed on{' '}
              <code>{repoFullName}</code> yet. Install it, then this panel links the project automatically.
            </p>
            <div className="rp-card__actions">
              <a className="rp-card__copy" href={installUrl} target="_blank" rel="noopener noreferrer" data-testid="project-github-install" data-analytics-event="chat-project-github-install">
                <Github size={13} aria-hidden /> Set up the GitHub App <ExternalLink size={12} aria-hidden />
              </a>
            </div>
            <div className="rp-card__waiting">
              <Spinner size={13} className="rp-card__icon" />
              <span>Waiting for the connection… detected automatically.</span>
            </div>
          </>
        )
      )}
      {displayError && <p className="rp-card__text">{displayError}</p>}
    </div>
  )
}
